import { checkRound, rtpPercent } from '../game/fair';
import { type MessageKey, t } from '../i18n';
import { api } from '../net/api';
import type { RoundResult } from '../net/protocol';
import { formatMultiplier } from '../util/format';
import type { Menu } from './menu';

/**
 * The provably-fair page in the drawer.
 *
 * Three things are on it, in the order they matter to a player who has just lost
 * a round and wants to know why:
 *
 *   1. the commitment for the round being played -- the number that was
 *      published before any stake was taken;
 *   2. the commitment for the *next* round, which is already sealed. This is the
 *      part most implementations of this screen do not have, and it is the
 *      strongest thing we can show: the outcome of the round after this one is
 *      already fixed and we have said so in advance;
 *   3. any round, checked in this browser.
 *
 * The check is done here rather than read off the server's answer. The endpoint
 * does reply `verified: true`, but that is the server agreeing with itself; the
 * scheme is only a proof if the player's own machine can redo the arithmetic. So
 * the seed and the commitment come from the API and the verdict comes from
 * game/fair.ts.
 *
 * This replaced a `window.alert()` that dumped the same values as one string. On
 * iOS an alert freezes the game behind it, its text cannot be copied, and it is
 * drawn in the browser's language rather than the player's.
 */
export interface Commitments {
  nonce: number;
  commitHash: string;
  nextNonce: number;
  nextCommitHash: string;
}

export interface FairPanelSources {
  /** The commitments for the current and next round, or null before the first. */
  commitments: () => Commitments | null;
  /** The most recent round this client watched settle, if any. */
  lastResult: () => RoundResult | null;
}

export class FairPanel {
  constructor(
    private readonly menu: Menu,
    private readonly sources: FairPanelSources,
  ) {}

  /** Opens the page. `nonce` pre-fills the verifier, for a click on a result. */
  show(nonce?: number): void {
    this.menu.showPage('menu.fair', (host) => this.build(host, nonce));
  }

  private build(host: HTMLElement, prefill?: number): void {
    host.replaceChildren();
    // The return to player is a figure the page publishes, and it is settable
    // from the back office -- so it is read from the last round this client
    // watched settle rather than written into the sentence. A client that has not
    // seen a round yet shows the default, which is what a round would be sealed
    // with if nothing had been configured.
    host.appendChild(
      text('p', t('fair.intro', { rtp: rtpPercent(this.sources.lastResult()?.rtp) })),
    );

    const commitments = this.sources.commitments();
    if (commitments) {
      host.appendChild(
        section('fair.thisRound', [
          labelled(t('fair.round', { nonce: commitments.nonce })),
          copyRow(t('verify.commitment'), commitments.commitHash),
        ]).wrap,
      );
      host.appendChild(
        section('fair.nextRound', [
          labelled(t('fair.round', { nonce: commitments.nextNonce })),
          copyRow(t('verify.commitment'), commitments.nextCommitHash),
          text('p', t('fair.nextRoundNote')),
        ]).wrap,
      );
    } else {
      host.appendChild(text('p', t('fair.pending')));
    }

    const last = this.sources.lastResult();
    if (last) host.appendChild(this.resultSection('fair.lastRound', last));

    host.appendChild(this.verifier(prefill ?? last?.nonce));
    host.appendChild(section('fair.formulaTitle', [text('p', t('fair.formula'))]).wrap);
  }

  /**
   * A settled round with its seed, and the verdict this browser reached about
   * it. The verdict is filled in asynchronously because HMAC via WebCrypto is a
   * promise; until it resolves the row says it is checking, never that it passed.
   */
  private resultSection(titleKey: MessageKey, result: RoundResult): HTMLElement {
    const verdict = document.createElement('div');
    verdict.className = 'verdict';
    verdict.textContent = t('fair.checking');

    const { wrap } = section(titleKey, [
      labelled(t('fair.round', { nonce: result.nonce })),
      labelled(t('verify.result', { multiplier: formatMultiplier(result.crashX100) })),
      copyRow(t('verify.commitment'), result.commitHash),
      copyRow(t('verify.seed'), result.serverSeed),
      verdict,
    ]);

    void checkRound(
      result.serverSeed,
      result.commitHash,
      result.nonce,
      result.crashX100,
      result.rtp,
    )
      .then((check) => {
        const ok = check.commitOk && check.crashOk;
        verdict.classList.add(ok ? 'pass' : 'fail');
        verdict.textContent = ok
          ? t('fair.localPass')
          : t('fair.localFail', { derived: formatMultiplier(check.derivedX100) });
      })
      .catch(() => {
        verdict.classList.add('fail');
        verdict.textContent = t('fair.checkFailed');
      });

    return wrap;
  }

  /** "Check round number N" -- the part that works for a round nobody watched. */
  private verifier(prefill?: number): HTMLElement {
    const { wrap, box } = section('fair.verifyTitle', []);

    const row = document.createElement('div');
    row.className = 'mono-row';

    const input = document.createElement('input');
    input.type = 'text';
    input.inputMode = 'numeric';
    input.autocomplete = 'off';
    input.className = 'nonce-input';
    input.placeholder = t('fair.verifyPrompt');
    if (prefill) input.value = String(prefill);

    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = t('fair.verifyButton');

    row.append(input, button);

    const output = document.createElement('div');
    output.className = 'verdict';

    const run = (): void => {
      const nonce = Number.parseInt(input.value.trim(), 10);
      if (!Number.isSafeInteger(nonce) || nonce < 1) {
        output.className = 'verdict fail';
        output.textContent = t('fair.badNonce');
        return;
      }

      output.className = 'verdict';
      output.textContent = t('fair.checking');
      button.disabled = true;

      void api
        .fairRound(nonce)
        .then(async ({ result }) => {
          const check = await checkRound(
            result.serverSeed,
            result.commitHash,
            result.nonce,
            result.crashX100,
            result.rtp,
          );
          const ok = check.commitOk && check.crashOk;
          output.className = `verdict ${ok ? 'pass' : 'fail'}`;
          output.textContent = ok
            ? `${t('verify.result', { multiplier: formatMultiplier(result.crashX100) })} — ${t('fair.localPass')}`
            : t('fair.localFail', { derived: formatMultiplier(check.derivedX100) });

          // The values themselves, so the player can take them elsewhere.
          box.appendChild(copyRow(t('verify.commitment'), result.commitHash));
          box.appendChild(copyRow(t('verify.seed'), result.serverSeed));
        })
        .catch(() => {
          output.className = 'verdict fail';
          output.textContent = t('verify.notFound');
        })
        .finally(() => {
          button.disabled = false;
        });
    };

    button.addEventListener('click', run);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') run();
    });

    box.append(row, output);
    return wrap;
  }
}

// ---------------------------------------------------------------------------
// Small builders. They are here rather than in menu.ts because they are the
// shape this page needs; a general-purpose widget kit for four pages would be
// more code than the pages.
// ---------------------------------------------------------------------------

function text(tag: 'p' | 'div', content: string): HTMLElement {
  const el = document.createElement(tag);
  el.textContent = content;
  return el;
}

function labelled(content: string): HTMLElement {
  const el = document.createElement('div');
  el.className = 'limit-row';
  el.textContent = content;
  return el;
}

/**
 * A titled block. The heading sits outside the box, so the caller gets both back
 * rather than having to dig the box out of the wrapper afterwards.
 */
function section(
  titleKey: MessageKey,
  children: HTMLElement[],
): { wrap: HTMLElement; box: HTMLElement } {
  const wrap = document.createElement('div');

  const heading = document.createElement('h3');
  heading.textContent = t(titleKey);
  wrap.appendChild(heading);

  const box = document.createElement('div');
  box.className = 'menu-block';
  box.append(...children);
  wrap.appendChild(box);

  return { wrap, box };
}

/**
 * A hash or a seed with a copy button.
 *
 * Copying is offered because these values are long, monospaced and the whole
 * point of them is that a player can take them away and check them somewhere
 * else. The clipboard API can be refused, in which case the button selects the
 * text instead of failing silently.
 */
function copyRow(label: string, value: string): HTMLElement {
  const wrap = document.createElement('div');

  const caption = document.createElement('h3');
  caption.textContent = label;

  const row = document.createElement('div');
  row.className = 'mono-row';

  const code = document.createElement('code');
  code.textContent = value;

  const button = document.createElement('button');
  button.type = 'button';
  button.textContent = t('fair.copy');
  button.addEventListener('click', () => {
    void navigator.clipboard
      ?.writeText(value)
      .then(() => {
        button.textContent = t('fair.copied');
        window.setTimeout(() => {
          button.textContent = t('fair.copy');
        }, 1200);
      })
      .catch(() => selectText(code));
  });

  row.append(code, button);
  wrap.append(caption, row);
  return wrap;
}

function selectText(el: HTMLElement): void {
  const range = document.createRange();
  range.selectNodeContents(el);
  const selection = window.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(range);
}
